import { CallDetailsModal } from '@/components/calls/call-details-modal';
import { CallsTable } from '@/components/calls/calls-table';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ASSISTENT_API_URL } from '@/config/backend';
import { useAuthStore } from '@/stores/auth-store';
import { useDevicesStore } from '@/stores/devices-store';
import { Call, CallOutcome, determineCallOutcome } from '@/types/calls';
import axios from 'axios';
import { endOfDay, format, startOfDay, subDays, subMonths } from 'date-fns';
import { Calendar, ChevronDown, Phone, Search } from "lucide-react";
import { useEffect, useMemo, useState } from 'react';
import { DateRange } from "react-day-picker";

// Debug: Track logged states
const loggedStates = new Set<string>();

export default function CallsPage() {
  const { currentDevice, currentDeviceSerialId } = useDevicesStore();
  const { getApiHeaders } = useAuthStore();

  // State
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<CallOutcome | 'all'>('all');
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showReviewed] = useState<'all' | 'reviewed' | 'unreviewed'>('unreviewed');

  // Default to last 7 days
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date())
  });
  const [dateRangeLabel, setDateRangeLabel] = useState('Last 7 days');

  // Load calls from API
  useEffect(() => {
    if (!currentDevice) return;
    loadCallData();
  }, [currentDevice, dateRange]);

  const loadCallData = async () => {
    if (!currentDevice) return;

    setLoading(true);
    setError(null);

    try {
      if (!dateRange?.from || !dateRange?.to) return;

      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');

      // Call the usage API endpoint (same as Activity page)
      const response = await axios.get(
        `${ASSISTENT_API_URL}device/${currentDeviceSerialId}/usage/${startDate}/${endDate}`,
        {
          headers: getApiHeaders()
        }
      );

      // Check for API errors
      if (response.data.errorCode !== null) {
        throw new Error(response.data.errorCode || 'API_ERROR');
      }

      // Transform API response to Call[] format
      // The usage API returns conversationsByState data
      const resultObject = response.data.resultObject || {};

      // Extract conversations from conversationsByState
      const conversationsByState = resultObject.conversationsByState || {};
      const conversationMap = new Map();

      // Combine all conversations from different states (deduplicate by sessionId)
      Object.keys(conversationsByState).forEach(state => {
        const stateConversations = conversationsByState[state] || [];
        stateConversations.forEach((conv: any) => {
          const id = conv.sessionId || conv.conversationId || conv.id;
          if (id && !conversationMap.has(id)) {
            conversationMap.set(id, conv);
          }
        });
      });

      const allConversations = Array.from(conversationMap.values());

      const transformedCalls: Call[] = allConversations.map((conv: any) => {
        // Extract actions from the conversation state
        const actions: any[] = [];

        // Map conversation state to action type
        if (conv.state) {
          actions.push({
            type: conv.state,
            timestamp: conv.conversationStartTime || new Date().toISOString(),
            data: conv
          });
        }

        // Handle forward state - map to CALL_FORWARDED if forwarded
        if (conv.conversationForwardState &&
          (conv.conversationForwardState === 'FORWARD_SUCCESS' ||
            conv.conversationForwardState === 'FORWARD_TRY')) {
          actions.push({
            type: 'CALL_FORWARDED',
            timestamp: conv.conversationStartTime || new Date().toISOString(),
            data: { ...conv, originalForwardState: conv.conversationForwardState }
          });
        }

        // DEBUG: Log unique states to see what we're getting from API
        if (conv.state && !loggedStates.has(conv.state)) {
          loggedStates.add(conv.state);
          console.log('📊 NEW STATE FOUND:', conv.state, '| Forward:', conv.conversationForwardState);
        }

        // Calculate end time from start time + duration
        const startTime = conv.conversationStartTime || new Date().toISOString();
        const endTime = conv.duration
          ? new Date(new Date(startTime).getTime() + conv.duration * 1000).toISOString()
          : startTime;

        // Combine booking date and time if available
        let bookingTime: string | undefined;
        if (conv.requestedBookingDate && conv.requestedBookingTime) {
          bookingTime = `${conv.requestedBookingDate}T${conv.requestedBookingTime}`;
        }

        return {
          sessionId: conv.sessionId || '',
          startTime: startTime,
          endTime: endTime,
          callerName: conv.customerName || 'Unbekannt',
          callerPhone: conv.callerNumber || '',
          bookingTime: bookingTime,
          guestCount: conv.requestedPersonCount || undefined,
          bookingId: conv.remoteBookingId || undefined,
          actions: actions,
          reviewed: false // TODO: Add reviewed field to API
        };
      });

      setCalls(transformedCalls);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No calls found - set empty array
        setCalls([]);
      } else {
        setError('Unable to load call data. Please try again later.');
        console.error('Failed to load calls:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter calls by search query
  const filteredCalls = useMemo(() => {
    if (!searchQuery) return calls;

    const query = searchQuery.toLowerCase();
    return calls.filter(call =>
      call.sessionId.toLowerCase().includes(query) ||
      call.callerName?.toLowerCase().includes(query) ||
      call.callerPhone?.toLowerCase().includes(query)
    );
  }, [calls, searchQuery]);

  // Calculate category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<CallOutcome | 'all', number> = {
      all: filteredCalls.length,
      BUSINESSDATA_DONE: 0,
      BUSINESSDATA_NOTCOMPLETED: 0,
      CONVERSATION_WITHOUT_BUSINESSDATA: 0,
      CALL_FORWARDED: 0,
      HANGUP_DIRECTLY: 0,
      OTHER: 0,
      UNKNOWN: 0
    };

    filteredCalls.forEach(call => {
      const outcome = determineCallOutcome(call.actions);
      counts[outcome]++;
    });

    return counts;
  }, [filteredCalls]);

  // Filter calls by active category - standardmäßig nur unreviewed
  const categoryFilteredCalls = useMemo(() => {
    let filtered = filteredCalls;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(call => {
        const outcome = determineCallOutcome(call.actions);
        return outcome === activeCategory;
      });
    }

    // Standardmäßig nur unreviewed calls zeigen
    if (showReviewed === 'unreviewed') {
      filtered = filtered.filter(call => !call.reviewed);
    } else if (showReviewed === 'all') {
      // Zeige alle (reviewed und unreviewed gemischt)
    } else {
      // Default: nur unreviewed
      filtered = filtered.filter(call => !call.reviewed);
    }

    return filtered;
  }, [filteredCalls, activeCategory, showReviewed]);

  // Get reviewed calls for the separate section
  const reviewedCallsForSection = useMemo(() => {
    let filtered = filteredCalls.filter(call => call.reviewed);

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(call => {
        const outcome = determineCallOutcome(call.actions);
        return outcome === activeCategory;
      });
    }

    return filtered;
  }, [filteredCalls, activeCategory]);

  // Handle call review
  const handleCallReview = (sessionId: string, reviewed: boolean) => {
    setCalls(prev => prev.map(call =>
      call.sessionId === sessionId ? { ...call, reviewed } : call
    ));
  };

  // Handle call details
  const handleCallDetails = (call: Call) => {
    setSelectedCall(call);
    setIsModalOpen(true);
  };

  // Handle modal navigation
  const handleModalNavigation = (direction: 'prev' | 'next') => {
    if (!selectedCall) return;

    const currentIndex = categoryFilteredCalls.findIndex(
      c => c.sessionId === selectedCall.sessionId
    );

    if (direction === 'prev' && currentIndex > 0) {
      setSelectedCall(categoryFilteredCalls[currentIndex - 1]);
    } else if (direction === 'next' && currentIndex < categoryFilteredCalls.length - 1) {
      setSelectedCall(categoryFilteredCalls[currentIndex + 1]);
    }
  };

  // Check if navigation is possible
  const canNavigatePrev = useMemo(() => {
    if (!selectedCall) return false;
    const currentIndex = categoryFilteredCalls.findIndex(
      c => c.sessionId === selectedCall.sessionId
    );
    return currentIndex > 0;
  }, [selectedCall, categoryFilteredCalls]);

  const canNavigateNext = useMemo(() => {
    if (!selectedCall) return false;
    const currentIndex = categoryFilteredCalls.findIndex(
      c => c.sessionId === selectedCall.sessionId
    );
    return currentIndex < categoryFilteredCalls.length - 1;
  }, [selectedCall, categoryFilteredCalls]);

  // Show device selection prompt if no device
  if (!currentDevice) {
    return (
      <div className="flex-1 p-6">
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No device selected</p>
              <p className="text-sm text-gray-500 mt-2">
                Please select a device from the header
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleDateRangePreset = (preset: 'last7days' | 'last30days' | 'last3months' | 'custom') => {
    const today = new Date();
    switch (preset) {
      case 'last7days':
        setDateRange({ from: startOfDay(subDays(today, 7)), to: endOfDay(today) });
        setDateRangeLabel('Last 7 days');
        break;
      case 'last30days':
        setDateRange({ from: startOfDay(subDays(today, 30)), to: endOfDay(today) });
        setDateRangeLabel('Last 30 days');
        break;
      case 'last3months':
        setDateRange({ from: startOfDay(subMonths(today, 3)), to: endOfDay(today) });
        setDateRangeLabel('Last 3 months');
        break;
    }
  }; return (
    <div className="flex-1 h-full bg-gray-50">
      {/* Header with breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Home</span>
          <span>›</span>
          <span className="font-semibold text-gray-900">Calls</span>
        </div>
      </div>

      {/* Date Range Picker and Search */}
      <div className="bg-gray-50 px-4 sm:px-6 py-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          {/* Date Range Picker */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="justify-start text-left font-normal w-full sm:w-48">
                <Calendar className="mr-2 h-4 w-4" />
                {dateRangeLabel}
                <ChevronDown className="ml-auto h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => handleDateRangePreset('last7days')}>
                Last 7 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDateRangePreset('last30days')}>
                Last 30 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDateRangePreset('last3months')}>
                Last 3 months
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Search by name, phone number or session-ID */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, phone number or session-ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Main content box with tabs and table */}
      <div className="px-4 sm:px-6 pb-6">
        <Tabs value={activeCategory} onValueChange={(v) => setActiveCategory(v as typeof activeCategory)} className="w-full">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            {/* Mobile: Dropdown für Kategorien */}
            <div className="lg:hidden border-b border-gray-200 p-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <span className="font-semibold">
                        {activeCategory === 'all' && 'All calls'}
                        {activeCategory === 'BUSINESSDATA_DONE' && 'Business Data Complete'}
                        {activeCategory === 'BUSINESSDATA_NOTCOMPLETED' && 'Business Data Incomplete'}
                        {activeCategory === 'CONVERSATION_WITHOUT_BUSINESSDATA' && 'Conversation Only'}
                        {activeCategory === 'CALL_FORWARDED' && 'Call Forwarded'}
                        {activeCategory === 'HANGUP_DIRECTLY' && 'Hung Up Directly'}
                        {activeCategory === 'OTHER' && 'Other'}
                        {activeCategory === 'UNKNOWN' && 'Unknown'}
                      </span>
                      <span className="text-lg font-bold text-brand-primary">
                        ({activeCategory === 'all' ? categoryCounts.all : categoryCounts[activeCategory]})
                      </span>
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[calc(100vw-2rem)]" align="start">
                  <DropdownMenuItem onClick={() => setActiveCategory('all')}>
                    <span className="flex items-center justify-between w-full">
                      <span>All calls</span>
                      <span className="font-bold text-brand-primary">{categoryCounts.all}</span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveCategory('BUSINESSDATA_DONE')}>
                    <span className="flex items-center justify-between w-full">
                      <span>Completed bookings</span>
                      <span className="font-bold text-brand-primary">{categoryCounts.BUSINESSDATA_DONE}</span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveCategory('BUSINESSDATA_NOTCOMPLETED')}>
                    <span className="flex items-center justify-between w-full">
                      <span>Incomplete bookings</span>
                      <span className="font-bold text-brand-primary">{categoryCounts.BUSINESSDATA_NOTCOMPLETED}</span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveCategory('CONVERSATION_WITHOUT_BUSINESSDATA')}>
                    <span className="flex items-center justify-between w-full">
                      <span>Questions only</span>
                      <span className="font-bold text-brand-primary">{categoryCounts.CONVERSATION_WITHOUT_BUSINESSDATA}</span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveCategory('CALL_FORWARDED')}>
                    <span className="flex items-center justify-between w-full">
                      <span>Forwarded</span>
                      <span className="font-bold text-brand-primary">{categoryCounts.CALL_FORWARDED}</span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveCategory('HANGUP_DIRECTLY')}>
                    <span className="flex items-center justify-between w-full">
                      <span>Hang-ups</span>
                      <span className="font-bold text-brand-primary">{categoryCounts.HANGUP_DIRECTLY}</span>
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Desktop: Tabs wie gewohnt */}
            <TabsList className="hidden lg:inline-flex h-auto items-center justify-start rounded-none bg-transparent border-b border-gray-200 p-0 gap-0 w-full overflow-x-auto">
              <TabsTrigger
                value="all"
                className="flex flex-col items-center px-6 py-3 border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent rounded-none hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl font-bold text-gray-900">{categoryCounts.all}</span>
                <span className="text-xs font-medium text-gray-600 mt-0.5">All calls</span>
              </TabsTrigger>
              <TabsTrigger
                value="BUSINESSDATA_DONE"
                className="flex flex-col items-center px-6 py-3 border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent rounded-none hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl font-bold text-gray-900">{categoryCounts.BUSINESSDATA_DONE}</span>
                <span className="text-xs font-medium text-gray-600 mt-0.5">Completed bookings</span>
              </TabsTrigger>
              <TabsTrigger
                value="BUSINESSDATA_NOTCOMPLETED"
                className="flex flex-col items-center px-6 py-3 border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent rounded-none hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl font-bold text-gray-900">{categoryCounts.BUSINESSDATA_NOTCOMPLETED}</span>
                <span className="text-xs font-medium text-gray-600 mt-0.5">Incomplete bookings</span>
              </TabsTrigger>
              <TabsTrigger
                value="CONVERSATION_WITHOUT_BUSINESSDATA"
                className="flex flex-col items-center px-6 py-3 border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent rounded-none hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl font-bold text-gray-900">{categoryCounts.CONVERSATION_WITHOUT_BUSINESSDATA}</span>
                <span className="text-xs font-medium text-gray-600 mt-0.5">Questions only</span>
              </TabsTrigger>
              <TabsTrigger
                value="CALL_FORWARDED"
                className="flex flex-col items-center px-6 py-3 border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent rounded-none hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl font-bold text-gray-900">{categoryCounts.CALL_FORWARDED}</span>
                <span className="text-xs font-medium text-gray-600 mt-0.5">Forwarded</span>
              </TabsTrigger>
              <TabsTrigger
                value="HANGUP_DIRECTLY"
                className="flex flex-col items-center px-6 py-3 border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent rounded-none hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl font-bold text-gray-900">{categoryCounts.HANGUP_DIRECTLY}</span>
                <span className="text-xs font-medium text-gray-600 mt-0.5">Hang-ups</span>
              </TabsTrigger>
              <TabsTrigger
                value="OTHER"
                className="flex flex-col items-center px-6 py-3 border-b-2 border-transparent data-[state=active]:border-brand-primary data-[state=active]:bg-transparent rounded-none hover:bg-gray-50 transition-colors"
              >
                <span className="text-xl font-bold text-gray-900">{categoryCounts.OTHER}</span>
                <span className="text-xs font-medium text-gray-600 mt-0.5">Other</span>
              </TabsTrigger>
            </TabsList>

            {/* Table content - same for all tabs, just filtered differently */}
            {['all', 'BUSINESSDATA_DONE', 'BUSINESSDATA_NOTCOMPLETED', 'CONVERSATION_WITHOUT_BUSINESSDATA', 'CALL_FORWARDED', 'HANGUP_DIRECTLY', 'OTHER'].map((tabValue) => (
              <TabsContent key={tabValue} value={tabValue} className="m-0 p-0">
                {loading ? (
                  <div className="p-12">
                    <div className="text-center">
                      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-primary border-r-transparent"></div>
                      <p className="text-gray-600 mt-4">Loading calls...</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="p-12">
                    <div className="text-center">
                      <p className="text-red-600 font-medium">{error}</p>
                      <Button onClick={loadCallData} className="mt-4">
                        Try again
                      </Button>
                    </div>
                  </div>
                ) : (
                  <CallsTable
                    calls={categoryFilteredCalls}
                    category={activeCategory}
                    onCallReview={handleCallReview}
                    onCallDetails={handleCallDetails}
                  />
                )}
              </TabsContent>
            ))}
          </div>
        </Tabs>

        {/* Reviewed Section - collapsible wie im Mockup */}
        {reviewedCallsForSection.length > 0 && (
          <details className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm group">
            <summary className="flex cursor-pointer items-center justify-between px-6 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Reviewed</span>
                <ChevronDown className="h-4 w-4 text-gray-500 transition-transform group-open:rotate-180" />
              </div>
              <span className="text-xs font-medium text-gray-500">{reviewedCallsForSection.length} calls</span>
            </summary>
            <div className="border-t border-gray-200">
              <CallsTable
                calls={reviewedCallsForSection}
                category={activeCategory}
                onCallReview={handleCallReview}
                onCallDetails={handleCallDetails}
              />
            </div>
          </details>
        )}
      </div>

      {/* Call Details Modal */}
      <CallDetailsModal
        call={selectedCall}
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCall(null);
        }}
        onNavigate={handleModalNavigation}
        canNavigatePrev={canNavigatePrev}
        canNavigateNext={canNavigateNext}
      />
    </div>
  );
}
